import Logo from "@/assets/logo.png";
import RobotArmImg from "@/assets/robot-arm.png";
import { LegalLink } from "@/components/legal-link.tsx";
import { SystemStatusBanner } from "@/components/navigation/system-status-banner.tsx";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { z } from "zod";
import { TypewriterText } from "../../components/type-writer-text.tsx";
import { m } from "@/paraglide/messages.js";
import { safeRedirectHref } from "@/shared/safe-redirect.ts";

/**
 * Headline slogans. Each entry is one set: a prefix that stays on screen for
 * the whole visit, and the endings that the typewriter cycles through beneath
 * it. Every ending has to finish the sentence its own prefix starts, which is
 * why they are grouped rather than pooled flat — an ending only ever appears
 * under the prefix it was written for.
 *
 * The set is chosen once per mount and does not change; only the typed line
 * moves. Two different rhythms on the same headline read as a glitch.
 *
 * The message functions are stored uncalled so the active locale is resolved at
 * render time rather than at module load.
 *
 * Keep prefixes short: they render `whitespace-nowrap` at `text-5xl` inside a
 * `max-w-md` column, so anything much past ~19 characters overflows.
 */
const SLOGANS = [
	{
		prefix: m.auth_slogan_1_prefix,
		texts: [
			m.auth_slogan_1_text_1,
			m.auth_slogan_1_text_2,
			m.auth_slogan_1_text_3,
			m.auth_slogan_1_text_4,
		],
	},
	{
		prefix: m.auth_slogan_2_prefix,
		texts: [
			m.auth_slogan_2_text_1,
			m.auth_slogan_2_text_2,
			m.auth_slogan_2_text_3,
			m.auth_slogan_2_text_4,
		],
	},
	{
		prefix: m.auth_slogan_3_prefix,
		texts: [
			m.auth_slogan_3_text_1,
			m.auth_slogan_3_text_2,
			m.auth_slogan_3_text_3,
			m.auth_slogan_3_text_4,
		],
	},
	{
		prefix: m.auth_slogan_4_prefix,
		texts: [
			m.auth_slogan_4_text_1,
			m.auth_slogan_4_text_2,
			m.auth_slogan_4_text_3,
			m.auth_slogan_4_text_4,
		],
	},
	{
		prefix: m.auth_slogan_5_prefix,
		texts: [
			m.auth_slogan_5_text_1,
			m.auth_slogan_5_text_2,
			m.auth_slogan_5_text_3,
			m.auth_slogan_5_text_4,
		],
	},
] as const;

export const Route = createFileRoute("/auth")({
	validateSearch: z.object({
		redirect: z.string().optional(),
	}),
	beforeLoad: async (opts) => {
		const { context, search, location } = opts;

		if (location.pathname === "/auth/oauth") {
			return;
		}

		if (context.auth.isAuthenticated) {
			throw redirect({ href: safeRedirectHref(search.redirect) });
		}
	},
	component: AuthLayoutComponent,
});

function AuthLayoutComponent() {
	// The set is drawn once and then held for the whole visit, so the prefix a
	// visitor lands on is the prefix they keep. A returning visitor meets a
	// different one because the draw happens per mount, not per cycle.
	const [slogan] = useState(
		() => SLOGANS[Math.floor(Math.random() * SLOGANS.length)] ?? SLOGANS[0],
	);
	// Only the typed line moves. Start it somewhere random too, so two visits
	// that happen to draw the same prefix still open differently.
	const [textIndex, setTextIndex] = useState(() =>
		Math.floor(Math.random() * slogan.texts.length),
	);
	const text = slogan.texts[textIndex] ?? slogan.texts[0];
	const advanceText = useCallback(() => {
		setTextIndex((current) => (current + 1) % slogan.texts.length);
	}, [slogan.texts.length]);

	return (
		<div className="dark flex min-h-screen flex-col bg-background text-foreground">
			{/* Pre-login platform notice; the endpoint is public. */}
			<SystemStatusBanner />

			<main className="grid flex-1 lg:grid-cols-[2fr_3fr]">
				<div className="relative hidden flex-col justify-between overflow-hidden border-sidebar-border border-r bg-sidebar p-12 lg:flex">
					{/* The robot arm, ghosted: a texture the headline sits on, not an
					    illustration competing with it — no glow, no tint, just presence. */}
					<div
						aria-hidden="true"
						className="pointer-events-none absolute inset-0 bg-contain bg-no-repeat opacity-[0.08]"
						style={{
							backgroundImage: `url(${RobotArmImg})`,
							backgroundPosition: "bottom right",
							backgroundSize: "78%",
						}}
					/>
					<img
						src={Logo}
						alt={m.auth_logo_alt()}
						className="relative h-9 w-auto self-start"
					/>
					<div className="relative max-w-md space-y-5">
						<h1 className="font-semibold text-4xl text-foreground leading-tight tracking-tight">
							<span className="block whitespace-nowrap">{slogan.prefix()}</span>
							<span className="block">
								<TypewriterText text={text()} onCycleComplete={advanceText} />
							</span>
						</h1>
						<p className="text-[15px] text-muted-foreground leading-relaxed">
							{m.auth_welcome_paragraph()}
						</p>
					</div>
					<a
						href="https://oneai.eu"
						target="_blank"
						rel="noopener noreferrer"
						className="relative font-medium text-s text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
					>
						{m.auth_learn_more()}
					</a>
				</div>
				<div className="flex flex-col items-center justify-center gap-8 p-6">
					<img
						src={Logo}
						alt={m.auth_logo_alt()}
						className="h-9 w-auto lg:hidden"
					/>
					<div className="w-full max-w-sm">
						<Outlet />
					</div>
					<div className="flex justify-center gap-4 text-muted-foreground text-xs">
						<LegalLink path="user-terms" />
						<LegalLink path="privacy" />
						<LegalLink path="imprint" />
					</div>
				</div>
			</main>
		</div>
	);
}
